import {useCallback, useLayoutEffect, useRef, useState} from 'react'
import Select, {Props as SelectProps} from 'react-select'
import {FilterOption} from './filters/reportFilterSchema'

const MENU_HEIGHT = 224
const MENU_GAP = 8
type MenuBounds = {placement: 'top' | 'bottom'; height: number}

export default function ReportFilterSelect(props: SelectProps<FilterOption, boolean>) {
  const container = useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState(false)
  const [menu, setMenu] = useState<MenuBounds>({placement: 'bottom', height: MENU_HEIGHT})
  const measureMenu = useCallback(() => {
    const control = container.current
    const body = control?.closest('.modal-body')
    if (!control || !body) return
    const field = control.getBoundingClientRect()
    const bounds = body.getBoundingClientRect()
    if (!field.height || !bounds.height) return
    const viewport = window.visualViewport
    const top = Math.max(bounds.top, viewport?.offsetTop || 0)
    const bottom = Math.min(
      bounds.bottom,
      viewport ? viewport.offsetTop + viewport.height : window.innerHeight
    )
    const above = Math.max(0, field.top - top - MENU_GAP)
    const below = Math.max(0, bottom - field.bottom - MENU_GAP)
    const placement = below >= MENU_HEIGHT || below >= above ? 'bottom' : 'top'
    const height = Math.max(
      1,
      Math.floor(Math.min(MENU_HEIGHT, placement === 'bottom' ? below : above))
    )
    setMenu((current) =>
      current.placement === placement && current.height === height ? current : {placement, height}
    )
  }, [])

  useLayoutEffect(() => {
    if (!open) return
    measureMenu()
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measureMenu)
    const body = container.current?.closest('.modal-body')
    if (container.current) observer?.observe(container.current)
    if (body) observer?.observe(body)
    window.addEventListener('resize', measureMenu)
    window.visualViewport?.addEventListener('resize', measureMenu)
    return () => {
      observer?.disconnect()
      window.removeEventListener('resize', measureMenu)
      window.visualViewport?.removeEventListener('resize', measureMenu)
    }
  }, [open, measureMenu])

  useLayoutEffect(() => {
    if (!open) return
    const values = container.current?.querySelector<HTMLElement>(
      '.select__value-container--is-multi'
    )
    // Keep the search input visible as additional chips wrap into the scroll area.
    if (values?.contains(document.activeElement)) values.scrollTop = values.scrollHeight
    measureMenu()
  }, [open, props.value, measureMenu])

  return (
    <div ref={container} className='report-filter-select'>
      <Select<FilterOption, boolean>
        {...props}
        hideSelectedOptions={Boolean(props.isMulti)}
        menuIsOpen={open}
        onMenuOpen={() => {
          measureMenu()
          setOpen(true)
          props.onMenuOpen?.()
        }}
        onMenuClose={() => {
          setOpen(false)
          props.onMenuClose?.()
        }}
        menuPosition='absolute'
        menuPlacement={menu.placement}
        maxMenuHeight={menu.height}
        minMenuHeight={menu.height}
        menuShouldScrollIntoView={false}
        closeMenuOnScroll={(event) => event.target === container.current?.closest('.modal-body')}
      />
    </div>
  )
}
